import { z } from 'zod';

/** Shared checkout contract. Values are customer input, never authorization. */
export const personalizationSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    age: z.number().int().min(1).max(18),
    language: z.string().trim().min(1).max(60),
    readingLevel: z.enum(['read_aloud', 'beginning', 'confident']),
    bookAbout: z.string().trim().min(1, 'Enter a book topic').max(1000),
  })
  .strict();
export type Personalization = z.infer<typeof personalizationSchema>;
export const personalizationKeys = {
  name: 'Child name',
  age: 'Child age',
  language: 'Book language',
  readingLevel: 'Reading level',
  bookAbout: 'Book topic',
} as const;
const readingLabels: Record<Personalization['readingLevel'], string> = {
  read_aloud: 'Read aloud together',
  beginning: 'Beginning reader',
  confident: 'Confident reader',
};
export function personalizationAttributes(input: Personalization) {
  const valid = personalizationSchema.parse(input);
  return Object.entries(personalizationKeys).map(([key, label]) => ({
    key: label,
    value:
      key === 'readingLevel'
        ? readingLabels[valid.readingLevel]
        : String(valid[key as keyof Personalization]),
  }));
}
export function parsePersonalization(
  properties: { name: string; value: unknown }[],
) {
  const values = Object.fromEntries(properties.map((p) => [p.name, p.value]));
  const input = Object.fromEntries(
    Object.entries(personalizationKeys).map(([key, label]) => [
      key,
      key === 'age' ? Number(values[label]) : values[label],
    ]),
  );
  input.readingLevel = Object.entries(readingLabels).find(
    ([, label]) => label === input.readingLevel,
  )?.[0];
  const result = personalizationSchema.safeParse(input);
  return result.success ? result.data : null;
}
